import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { APP_ROUTES } from '@backrs_ui/grow-ui/src/appRoutes';
import { AppRoute } from '@backrs_ui/grow-ui/src/appRoutes.constants';

type CompiledRoute = Readonly<{ route: AppRoute; regex: RegExp }>;

const paramCount = (path: string): number => (path.match(/:[^/]+/g) ?? []).length;

const COMPILED_ROUTES: readonly CompiledRoute[] = Object.values(APP_ROUTES)
  .filter((route) => route.path !== '*')
  // Rank by specificity: a route with fewer dynamic params wins over a sibling
  // with more (so '/coaching-dashboard' beats '/coaching-dashboard/:memberId'),
  // then by path length so deeper static routes win over '/'.
  .sort((a, b) => paramCount(a.path) - paramCount(b.path) || b.path.length - a.path.length)
  .map((route) => {
    const pattern = route.path
      .replace(/:[^/]+\?/g, '[^/]*') // optional params
      .replace(/:[^/]+/g, '[^/]+'); // required params
    const regex = route.subRoute
      ? new RegExp(`^${pattern}(/[^/]+)?/?$`) // allow optional subroute segment
      : new RegExp(`^${pattern}/?$`);

    return { route, regex };
  });

export const useCurrentAppRoute = (): AppRoute | undefined => {
  const { pathname } = useLocation();

  return useMemo(
    () => COMPILED_ROUTES.find(({ regex }) => regex.test(pathname))?.route,
    [pathname],
  );
};
